import { ColorUtils } from '../utils/ColorUtils';
/**
 * The ColorTransform export class lets you adjust the color values in a display
 * object. The color adjustment or <i>color transformation</i> can be applied
 * to all four channels: red, green, blue, and alpha transparency.
 *
 * <p>When a ColorTransform object is applied to a display object, a new value
 * for each color channel is calculated like this:</p>
 *
 * <ul>
 *   <li>New red value = (old red value * <code>redMultiplier</code>) +
 * <code>redOffset</code></li>
 *   <li>New green value = (old green value * <code>greenMultiplier</code>) +
 * <code>greenOffset</code></li>
 *   <li>New blue value = (old blue value * <code>blueMultiplier</code>) +
 * <code>blueOffset</code></li>
 *   <li>New alpha value = (old alpha value * <code>alphaMultiplier</code>) +
 * <code>alphaOffset</code></li>
 * </ul>
 *
 * <p>If any of the color channel values is greater than 255 after the
 * calculation, it is set to 255. If it is less than 0, it is set to 0.</p>
 *
 * <p>You can use ColorTransform objects in the following ways:</p>
 *
 * <ul>
 *   <li>In the <code>colorTransform</code> parameter of the
 * <code>colorTransform()</code> method of the BitmapData class</li>
 *   <li>As the <code>colorTransform</code> property of a Transform object
 * (which can be used as the <code>transform</code> property of a display
 * object)</li>
 * </ul>
 *
 * <p>You must use the <code>new ColorTransform()</code> constructor to create
 * a ColorTransform object before you can call the methods of the
 * ColorTransform object.</p>
 *
 * <p>Color transformations do not apply to the background color of a movie
 * clip(such as a loaded SWF object). They apply only to graphics and symbols
 * that are attached to the movie clip.</p>
 */
var ColorTransform = /** @class */ (function () {
    function ColorTransform(redMultiplier, greenMultiplier, blueMultiplier, alphaMultiplier, redOffset, greenOffset, blueOffset, alphaOffset) {
        /* eslint-enable */
        if (redMultiplier === void 0) { redMultiplier = 1; }
        if (greenMultiplier === void 0) { greenMultiplier = 1; }
        if (blueMultiplier === void 0) { blueMultiplier = 1; }
        if (alphaMultiplier === void 0) { alphaMultiplier = 1; }
        if (redOffset === void 0) { redOffset = 0; }
        if (greenOffset === void 0) { greenOffset = 0; }
        if (blueOffset === void 0) { blueOffset = 0; }
        if (alphaOffset === void 0) { alphaOffset = 0; }
        this._isDirty = false;
        if (redMultiplier instanceof Float32Array) {
            this._rawData = redMultiplier;
        }
        else {
            this._rawData = new Float32Array(8);
            this.redMultiplier = Number(redMultiplier);
            this.greenMultiplier = greenMultiplier;
            this.blueMultiplier = blueMultiplier;
            this.alphaMultiplier = alphaMultiplier;
            this.redOffset = redOffset;
            this.greenOffset = greenOffset;
            this.blueOffset = blueOffset;
            this.alphaOffset = alphaOffset;
        }
    }
    Object.defineProperty(ColorTransform.prototype, "isDirty", {
        get: function () {
            return this._isDirty;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "isIdentity", {
        get: function () {
            return !this._isDirty;
        },
        enumerable: true,
        configurable: true
    });
    ColorTransform.prototype.concat = function (second) {
        this._rawData[0] *= second._rawData[0];
        this._rawData[1] *= second._rawData[1];
        this._rawData[2] *= second._rawData[2];
        this._rawData[3] *= second._rawData[3];
        this._rawData[4] += second._rawData[4];
        this._rawData[5] += second._rawData[5];
        this._rawData[6] += second._rawData[6];
        this._rawData[7] += second._rawData[7];
        this._isDirty = this._isDirty || second._isDirty;
    };
    Object.defineProperty(ColorTransform.prototype, "alphaMultiplier", {
        /**
         * A decimal value that is multiplied with the alpha transparency channel
         * value.
         *
         * <p>If you set the alpha transparency value of a display object directly by
         * using the <code>alpha</code> property of the DisplayObject instance, it
         * affects the value of the <code>alphaMultiplier</code> property of that
         * display object's <code>transform.colorTransform</code> property.</p>
         */
        get: function () {
            return this._rawData[3];
        },
        set: function (value) {
            this._rawData[3] = value;
            this._isDirty = this._isDirty || value !== 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "alphaOffset", {
        /**
         * A number from -255 to 255 that is added to the alpha transparency channel
         * value after it has been multiplied by the <code>alphaMultiplier</code>
         * value.
         */
        get: function () {
            return this._rawData[7];
        },
        set: function (value) {
            this._rawData[7] = value;
            this._isDirty = this._isDirty || value !== 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "blueMultiplier", {
        /**
         * A decimal value that is multiplied with the blue channel value.
         */
        get: function () {
            return this._rawData[2];
        },
        set: function (value) {
            this._rawData[2] = value;
            this._isDirty = this._isDirty || value !== 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "blueOffset", {
        /**
         * A number from -255 to 255 that is added to the blue channel value after it
         * has been multiplied by the <code>blueMultiplier</code> value.
         */
        get: function () {
            return this._rawData[6];
        },
        set: function (value) {
            this._rawData[6] = value;
            this._isDirty = this._isDirty || value !== 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "greenMultiplier", {
        /**
         * A decimal value that is multiplied with the green channel value.
         */
        get: function () {
            return this._rawData[1];
        },
        set: function (value) {
            this._rawData[1] = value;
            this._isDirty = this._isDirty || value !== 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "greenOffset", {
        /**
         * A number from -255 to 255 that is added to the green channel value after
         * it has been multiplied by the <code>greenMultiplier</code> value.
         */
        get: function () {
            return this._rawData[5];
        },
        set: function (value) {
            this._rawData[5] = value;
            this._isDirty = this._isDirty || value !== 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "redMultiplier", {
        /**
         * A decimal value that is multiplied with the red channel value.
         */
        get: function () {
            return this._rawData[0];
        },
        set: function (value) {
            this._rawData[0] = value;
            this._isDirty = this._isDirty || value !== 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "redOffset", {
        /**
         * A number from -255 to 255 that is added to the red channel value after it
         * has been multiplied by the <code>redMultiplier</code> value.
         */
        get: function () {
            return this._rawData[4];
        },
        set: function (value) {
            this._rawData[4] = value;
            this._isDirty = this._isDirty || value !== 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "color", {
        /**
         * The RGB color value for a ColorTransform object.
         *
         * <p>When you set this property, it changes the three color offset values
         * (<code>redOffset</code>, <code>greenOffset</code>, and
         * <code>blueOffset</code>) accordingly, and it sets the three color
         * multiplier values(<code>redMultiplier</code>,
         * <code>greenMultiplier</code>, and <code>blueMultiplier</code>) to 0. The
         * alpha transparency multiplier and offset values do not change.</p>
         *
         * <p>When you pass a value for this property, use the format
         * 0x<i>RRGGBB</i>. <i>RR</i>, <i>GG</i>, and <i>BB</i> each consist of two
         * hexadecimal digits that specify the offset of each color component. The 0x
         * tells the ActionScript compiler that the number is a hexadecimal
         * value.</p>
         */
        get: function () {
            return ((this._rawData[4] << 16) | (this._rawData[5] << 8) | this._rawData[6]);
        },
        set: function (value) {
            var argb = ColorUtils.float32ColorToARGB(value);
            this._rawData[4] = argb[1]; //(value >> 16) & 0xFF;
            this._rawData[5] = argb[2]; //(value >> 8) & 0xFF;
            this._rawData[6] = argb[3]; //value & 0xFF;
            /*
            * ZERO!!! Because this is overidded color.
            * This means that sprite will be rendered only with this color
            */
            this._rawData[0] = 0;
            this._rawData[1] = 0;
            this._rawData[2] = 0;
            this._isDirty = true;
        },
        enumerable: true,
        configurable: true
    });
    ColorTransform.prototype.copyRawDataFrom = function (sourceData) {
        var targetData = this._rawData;
        targetData[0] = sourceData[0];
        targetData[1] = sourceData[1];
        targetData[2] = sourceData[2];
        targetData[3] = sourceData[3];
        targetData[4] = sourceData[4];
        targetData[5] = sourceData[5];
        targetData[6] = sourceData[6];
        targetData[7] = sourceData[7];
        // todo deep check
        this._isDirty = true;
    };
    ColorTransform.prototype.clear = function () {
        this._rawData[0] = 1;
        this._rawData[1] = 1;
        this._rawData[2] = 1;
        this._rawData[3] = 1;
        this._rawData[4] = 0;
        this._rawData[5] = 0;
        this._rawData[6] = 0;
        this._rawData[7] = 0;
        this._isDirty = false;
    };
    ColorTransform.prototype.clone = function () {
        var colorTransform = new ColorTransform();
        colorTransform.copyFrom(this);
        return colorTransform;
    };
    ColorTransform.prototype.copyFrom = function (source) {
        var sourceData = source._rawData, targetData = this._rawData;
        targetData[0] = sourceData[0];
        targetData[1] = sourceData[1];
        targetData[2] = sourceData[2];
        targetData[3] = sourceData[3];
        targetData[4] = sourceData[4];
        targetData[5] = sourceData[5];
        targetData[6] = sourceData[6];
        targetData[7] = sourceData[7];
        this._isDirty = source._isDirty;
    };
    ColorTransform.prototype.copyTo = function (target) {
        target.copyFrom(this);
    };
    ColorTransform.prototype.prepend = function (ct) {
        this._rawData[4] += ct._rawData[4] * this._rawData[0];
        this._rawData[5] += ct._rawData[5] * this._rawData[1];
        this._rawData[6] += ct._rawData[6] * this._rawData[2];
        this._rawData[7] += ct._rawData[7] * this._rawData[3];
        this.redMultiplier *= ct.redMultiplier;
        this.greenMultiplier *= ct.greenMultiplier;
        this.blueMultiplier *= ct.blueMultiplier;
        this.alphaMultiplier *= ct.alphaMultiplier;
        this._isDirty = this._isDirty || ct._isDirty;
    };
    ColorTransform.prototype._isRenderable = function () {
        return this._rawData[3] != 0 || this._rawData[7] > 0;
    };
    return ColorTransform;
}());
export { ColorTransform };
